import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { STATUS_META, formatDate } from "@/lib/status";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";

const STATUSES = ["NORMAL", "RESTRICTED", "LIMITED_VISITATION", "LOCKDOWN", "VISITATION_SUSPENDED", "EMERGENCY", "UNKNOWN"];

export default function AdminDashboardPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [selected, setSelected] = useState(null);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [scrapeBusy, setScrapeBusy] = useState(false);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin");
  }, [user, loading, navigate]);

  const refresh = () => {
    apiClient.get("/facilities").then(r => setFacilities(r.data));
    apiClient.get("/admin/audit").then(r => setAudit(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) refresh(); }, [user]);

  const updateStatus = async (facility, patch) => {
    try {
      const { data } = await apiClient.patch(`/admin/facilities/${facility.id}`, patch);
      toast.success("Facility updated");
      setFacilities(prev => prev.map(f => f.id === data.id ? data : f));
      if (selected?.id === data.id) setSelected(data);
      apiClient.get("/admin/audit").then(r => setAudit(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const scrape = async () => {
    if (!selected) return;
    setScrapeBusy(true);
    try {
      const { data } = await apiClient.post(`/admin/facilities/${selected.id}/scrape-status`, { url: scrapeUrl });
      toast.success(`AI status set to ${data.status}`);
      setSelected(data);
      setFacilities(prev => prev.map(f => f.id === data.id ? data : f));
      setScrapeOpen(false);
      setScrapeUrl("");
      apiClient.get("/admin/audit").then(r => setAudit(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scrape failed");
    } finally {
      setScrapeBusy(false);
    }
  };

  if (loading || !user) return <div className="p-8">Loading...</div>;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="admin-dashboard">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Admin dashboard</h1>
          <p className="text-sm text-slate-600">Manage facility statuses and trigger AI-scraped updates.</p>
        </div>
        <div className="font-mono text-xs text-slate-600">{user.email}</div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Facility list */}
        <Card className="p-3 lg:col-span-1 border-slate-200 max-h-[600px] overflow-y-auto">
          <div className="px-2 py-2 font-heading font-bold text-slate-900">Facilities ({facilities.length})</div>
          <div className="space-y-1">
            {facilities.map(f => (
              <button
                key={f.id}
                data-testid={`admin-facility-row-${f.id}`}
                onClick={() => setSelected(f)}
                className={`w-full text-left rounded-md p-2 hover:bg-slate-100 ${selected?.id === f.id ? "bg-slate-100" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{f.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 truncate">{f.city}, {f.state}</div>
                  </div>
                  <StatusBadge status={f.status} size="sm" />
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Editor */}
        <Card className="p-5 lg:col-span-2 border-slate-200">
          {!selected && <p className="text-slate-600 text-sm">Select a facility to edit.</p>}
          {selected && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{selected.system}</div>
                  <h2 className="font-heading text-xl font-bold text-slate-900">{selected.name}</h2>
                </div>
                <StatusBadge status={selected.status} />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Status</Label>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected, { status: v })}>
                    <SelectTrigger data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visitation status</Label>
                  <Input
                    data-testid="admin-visitation-status-input"
                    defaultValue={selected.visitation_status || ""}
                    onBlur={(e) => e.target.value !== (selected.visitation_status || "") && updateStatus(selected, { visitation_status: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Status note</Label>
                  <Textarea
                    data-testid="admin-status-note-input"
                    defaultValue={selected.status_note || ""}
                    onBlur={(e) => e.target.value !== (selected.status_note || "") && updateStatus(selected, { status_note: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Source name</Label>
                  <Input
                    data-testid="admin-source-name-input"
                    defaultValue={selected.status_source || ""}
                    onBlur={(e) => e.target.value !== (selected.status_source || "") && updateStatus(selected, { status_source: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Source URL</Label>
                  <Input
                    data-testid="admin-source-url-input"
                    defaultValue={selected.status_source_url || ""}
                    onBlur={(e) => e.target.value !== (selected.status_source_url || "") && updateStatus(selected, { status_source_url: e.target.value })}
                  />
                </div>
              </div>

              <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-center gap-2 font-heading font-bold text-slate-900">
                  <Wand2 size={16} className="text-blue-700" /> AI-scrape status from official page
                </div>
                <p className="mt-1 text-xs text-slate-700">Provide an official DOC / BOP URL. Claude will read the page and update the status.</p>
                <div className="mt-3">
                  <Button
                    data-testid="admin-open-scrape-btn"
                    onClick={() => { setScrapeUrl(selected.status_source_url || selected.website || ""); setScrapeOpen(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Run AI scrape
                  </Button>
                </div>
                <div className="mt-3 text-[11px] font-mono text-slate-600">
                  Last update: {formatDate(selected.status_updated_at)} · Source: {selected.status_source || "—"}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Audit log */}
      <Card className="mt-6 p-5 border-slate-200">
        <h2 className="font-heading text-lg font-bold text-slate-900">Recent status changes</h2>
        <div className="mt-3 space-y-2 text-sm">
          {audit.length === 0 && <p className="text-slate-500 text-sm">No changes yet.</p>}
          {audit.slice(0, 20).map(a => (
            <div key={a.id} className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{formatDate(a.at)}</div>
              <div className="mt-1 text-xs text-slate-800">
                <span className="font-semibold">Facility:</span> {a.facility_id}
              </div>
              <div className="mt-1 text-xs text-slate-700 break-words">
                {Object.entries(a.updates || {}).filter(([k]) => k !== "ai_raw").map(([k, v]) => (
                  <span key={k} className="mr-3"><span className="font-semibold">{k}</span>: {String(v).slice(0, 100)}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={scrapeOpen} onOpenChange={setScrapeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>AI-scrape official page</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Official page URL</Label>
            <Input data-testid="scrape-url-input" value={scrapeUrl} onChange={(e) => setScrapeUrl(e.target.value)} placeholder="https://www.bop.gov/locations/institutions/..." />
            <p className="text-xs text-slate-500">Claude will read the page and extract the current status &amp; visitation info.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScrapeOpen(false)}>Cancel</Button>
            <Button data-testid="scrape-run-btn" onClick={scrape} disabled={scrapeBusy || !scrapeUrl}>
              {scrapeBusy ? "Analyzing..." : "Run scrape"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
